import { TimeblocksByRangeWithGeoQuery } from "../graphql/generated";

type Timeblock =
  TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][number]["timeblocks"][number];

export const groupTimeblocksDataByWeekday = (
  timeblocksData: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][0]["timeblocks"],
) => {
  if (!timeblocksData) {
    return [];
  }

  const groupedByWeekday = timeblocksData.reduce(
    (acc: { [key: number]: typeof timeblocksData }, timeblock) => {
      const weekday = timeblock.weekday;

      if (!acc[weekday]) {
        acc[weekday] = [];
      }

      acc[weekday].push(timeblock);

      return acc;
    },
    {} as { [key: number]: typeof timeblocksData },
  );

  return Object.entries(groupedByWeekday).map(([weekday, timeblocks]) => ({
    weekday: Number(weekday),
    timeblocks,
  }));
};

export const filterAvailableWeeklyTimeblocks = (
  weeks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const firstAvailableIdx = weeks.findIndex(({ timeblocks }) =>
    timeblocks.some((t) => !(t.noDelivery && t.fallbackTimeblockId === null)),
  );

  return weeks.slice(firstAvailableIdx, firstAvailableIdx + 4);
};

/**
 * A valid timeblock is one that:
 * - is not a fallback (i.e., `fallbackTimeblockId` is null or undefined)
 * - allows delivery (i.e., `noDelivery` is false)
 *
 * When there are fallback timeblocks, the backend inserts them into the array.
 * These fallback blocks typically have `noDelivery: true` and need to be excluded.
 * @param t - The timeblock to evaluate
 * @returns True if the timeblock is valid, false otherwise
 */
const isValidTimeblock = (t: Timeblock): boolean =>
  !t.fallbackTimeblockId && !t.noDelivery;

/**
 * Returns the list of valid, deliverable timeblocks from the first week that contains them.
 *
 * @param weeks - An array of weeks, each containing a list of timeblocks
 * @returns An array of timeblocks from the first week that has at least one valid timeblock.
 *          Returns an empty array if no valid timeblocks are found in any week.
 */
export const getFirstAvailableTimeblocks = (
  weeks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const week = weeks.find((w) => w.timeblocks.some(isValidTimeblock));

  return week?.timeblocks?.filter(isValidTimeblock) ?? [];
};
